import { useTranslation } from "next-i18next";
import React from "react";

import { useI18NContext } from "@/features/common/components/i18n/I18NProvider";
import { FormatArgs } from "@/features/common/components/i18n/type";
import { CurrencyEnum } from "@/generated/generated-hooks";
import {
  addMonthsTz,
  getStartAndEndOfMonth,
} from "@/helpers/common/dateTimeHelper";

import { JoinSubscriptionBoxView } from "./JoinSubscriptionBoxView";

type JoinSubscriptionSummaryViewProps = {
  title: string;
  price: number;
  currency: CurrencyEnum;
  isUnlockExpiredPost?: boolean;
};

export const JoinSubscriptionSummaryView: React.FC<JoinSubscriptionSummaryViewProps> =
  ({ title, currency, price, isUnlockExpiredPost }) => {
    const { formatWithLocale, ready } = useI18NContext();
    const { t } = useTranslation("subscriptionPlan");

    return (
      <JoinSubscriptionBoxView>
        {/* Title */}
        <div className="w-full flex items-center mb-2">
          <h5 className="text-2xl">{t("summary")}</h5>
        </div>

        {/* Name of Subscription plan */}
        <div className="w-full flex items-center justify-between mb-2">
          <span className="text-base">{title}</span>
          <span className="text-base font-bold">{`${currency} ${price}`}</span>
        </div>

        {/* Today's charge */}
        {isUnlockExpiredPost ? (
          <>
            <div className="w-full flex items-center justify-between mb-2">
              <span className="text-base">{t("purchaseSubscription")}</span>
            </div>

            {/* Renewal date */}
            <div className="w-full flex items-center justify-between mb-2">
              <span className="text-base">
                {t("subscriptionPlan:renew", {
                  date: t("notApplicable"),
                })}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="w-full flex items-center justify-between mb-2">
              <span className="text-base">{t("todaysCharge")}</span>
              <span className="text-base font-bold">
                {`${currency} ${price}`}
              </span>
            </div>

            {/* Renewal date */}
            <div className="w-full flex items-center justify-between mb-2">
              <span className="text-base">
                {ready
                  ? t("subscriptionPlan:renew", {
                      date: getRenewDate(
                        formatWithLocale,
                        t("renewDateFormat"),
                      ),
                    })
                  : ""}
              </span>
            </div>
          </>
        )}
      </JoinSubscriptionBoxView>
    );
  };

function getRenewDate(
  formatWithLocale: (args: FormatArgs) => string,
  formatStr: string,
) {
  const dateAfterOneMonth = addMonthsTz(new Date(), 1);

  const { startAt } = getStartAndEndOfMonth(dateAfterOneMonth);

  return formatWithLocale({
    date: startAt,
    formatStr,
  });
}
